import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import {
  ChevronLeft, Loader2, Sparkles, RefreshCw, Save, Copy, AlertCircle, Clock,
  Zap, DollarSign, CheckCircle2, ListTree, FileText, X,
} from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const STATUS = {
  pending: { label: "Sıraya alındı", color: "ky-badge-neutral" },
  generating: { label: "Üretiliyor", color: "ky-badge-warning" },
  done: { label: "Tamamlandı", color: "ky-badge-success" },
  failed: { label: "Başarısız", color: "ky-badge-danger" },
};

export default function AdCopyJobDetailPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [selectedH, setSelectedH] = useState(new Set());
  const [selectedD, setSelectedD] = useState(new Set());
  const [regenItem, setRegenItem] = useState(null); // { type, id }
  const [regenInstr, setRegenInstr] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [saveModal, setSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCustomerId, setSaveCustomerId] = useState("");
  const [allCustomers, setAllCustomers] = useState([]);
  const pollRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const errCountRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/ai-tools/ad-copy/jobs/${jobId}`);
      setJob(data); setLoadError(null); errCountRef.current = 0;
    } catch (e) {
      const status = e?.response?.status;
      const isTransient = !status || [502, 503, 504, 408, 429].includes(status);
      errCountRef.current += 1;
      if (isTransient) setLoadError({ kind: "transient", msg: `Bağlantı yeniden kuruluyor… (${errCountRef.current}. deneme)` });
      else if (errCountRef.current === 1) { toast.error(formatApiError(e)); setLoadError({ kind: "fatal", msg: formatApiError(e) }); }
    }
  };
  useEffect(() => { load(); pollRef.current = setInterval(load, 3000); apiClient.get("/customers").then(r => setAllCustomers(r.data||[])).catch(()=>{}); return () => clearInterval(pollRef.current); }, [jobId]); // eslint-disable-line
  useEffect(() => { if (job?.status === "done" || job?.status === "failed") { if (pollRef.current) clearInterval(pollRef.current); } }, [job?.status]);

  if (!job) return <AdCopySkeleton loadError={loadError}/>;

  const result = job.result || {};
  const headlines = result.headlines || [];
  const descriptions = result.descriptions || [];
  const st = STATUS[job.status] || STATUS.pending;
  const inProgress = job.status === "pending" || job.status === "generating";

  const toggle = (set, setter, id) => {
    const ns = new Set(set);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setter(ns);
  };
  const selectAll = (items, setter) => setter(new Set(items.map(i=>i.id)));
  const clearSel = (setter) => setter(new Set());

  const regenerate = async () => {
    if (!regenItem) return;
    setRegenLoading(true);
    try {
      await apiClient.post(`/ai-tools/ad-copy/jobs/${jobId}/regenerate`, {
        item_type: regenItem.type, item_id: regenItem.id, instruction: regenInstr,
      });
      toast.success("Yeniden üretildi");
      setRegenItem(null); setRegenInstr(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRegenLoading(false); }
  };

  const save = async () => {
    if (!saveName.trim()) return toast.error("Bir isim ver");
    if (!selectedH.size && !selectedD.size) return toast.error("En az bir öğe seç");
    const target = job.customer_id || saveCustomerId;
    if (!target) return toast.error("Müşteri seçimi zorunlu — set bir müşteriye bağlanmalı");
    try {
      await apiClient.post(`/ai-tools/ad-copy/jobs/${jobId}/save`, {
        name: saveName,
        customer_id: target,
        selected_headline_ids: [...selectedH],
        selected_description_ids: [...selectedD],
      });
      toast.success("Müşteri profiline kaydedildi");
      setSaveModal(false); setSaveName(""); setSaveCustomerId(""); setSelectedH(new Set()); setSelectedD(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyText = (txt) => { navigator.clipboard.writeText(txt); toast.success("Kopyalandı"); };
  const copyAll = (items) => { navigator.clipboard.writeText(items.map(i=>i.text).join("\n")); toast.success(`${items.length} öğe kopyalandı`); };

  return (
    <div className="space-y-5" data-testid="ad-copy-detail">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar/reklam-metni" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Reklam Metni Üretici</Link>
        <span>/</span><span className="font-medium text-[#0F172A]">İş #{jobId.slice(0,8)}</span>
      </div>

      <div className="ky-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-[#D97706]"/>
              <h1 className="text-xl font-bold">{job.tone || "Profesyonel"} ton · {job.headline_count}× başlık · {job.description_count}× açıklama</h1>
              <span className={`ky-badge ${st.color}`}>{st.label}</span>
            </div>
            <div className="text-xs text-[#64748B]">
              {(job.keywords||[]).length} pozitif · {(job.negative_keywords||[]).length} negatif kelime
              {job.customer_id && " · Müşteri atandı"}
            </div>
          </div>
          {job.status === "done" && (selectedH.size > 0 || selectedD.size > 0) && (
            <button onClick={()=>setSaveModal(true)} className="ky-btn ky-btn-accent" data-testid="save-selected">
              <Save size={13}/>Seçilenleri Kaydet ({selectedH.size + selectedD.size})
            </button>
          )}
        </div>

        {inProgress && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-medium"><Loader2 size={12} className="animate-spin"/>{job.status_message || "İşleniyor…"}</span>
              <span className="font-semibold text-[#4F46E5]">{job.progress || 0}%</span>
            </div>
            <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#4F46E5] to-[#D97706] transition-all" style={{ width: `${job.progress || 0}%` }}/>
            </div>
          </div>
        )}
        {(job.status === "done" || job.status === "failed") && job.cost && (
          <div className="mt-4 flex items-center justify-between flex-wrap gap-2 bg-[#FAFAFB] border border-[#ECECEF] rounded-lg px-3 py-2 text-xs">
            <div className="flex items-center gap-4 flex-wrap">
              {job.elapsed_seconds !== undefined && <span className="flex items-center gap-1.5 text-[#64748B]"><Clock size={12}/>Süre: <strong className="text-[#0F172A]">{Math.floor(job.elapsed_seconds/60)}dk {Math.floor(job.elapsed_seconds%60)}sn</strong></span>}
              <span className="flex items-center gap-1.5 text-[#64748B]"><Zap size={12}/>AI çağrısı: <strong className="text-[#0F172A]">{job.cost.calls || 0}</strong></span>
              <span className="flex items-center gap-1.5 text-[#64748B]"><DollarSign size={12}/>Maliyet: <strong className="text-[#0F172A]">${(job.cost.usd || 0).toFixed(4)}</strong> <span className="text-[#94A3B8]">(₺{(job.cost.try || 0).toFixed(2)})</span></span>
            </div>
          </div>
        )}
        {job.status === "failed" && (
          <div className="mt-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 text-xs text-[#991B1B] flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0"/>
            <div className="flex-1">
              <div>{job.status_message || "Bilinmeyen hata"}</div>
              {/^.*bütçe.*tükenmiş/i.test(job.status_message || "") && (
                <div className="mt-2 flex items-center gap-2">
                  <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-[#991B1B] text-white rounded text-[11px] font-medium hover:bg-[#7F1D1D]">
                    Anthropic Billing'i aç →
                  </a>
                  <span className="text-[10px] text-[#7F1D1D]">console.anthropic.com → Plans & Billing</span>
                </div>
              )}
            </div>
          </div>
        )}
        {job.status === "done" && (job.warnings || []).length > 0 && (
          <div className="mt-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 text-xs text-[#92400E]" data-testid="adcopy-warnings">
            <div className="flex items-center gap-2 font-semibold mb-1"><AlertCircle size={13}/>Kısmi başarı — {(job.warnings || []).length} uyarı</div>
            <ul className="list-disc ml-5 space-y-0.5">
              {(job.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <div className="mt-1.5 text-[10px] text-[#A16207]">İpucu: AI kotası/rate-limit nedeniyle bazı tonlar üretilemedi. Birkaç dakika bekleyip yeniden başlatabilirsin.</div>
          </div>
        )}
      </div>

      {job.status === "done" && (
        <>
          {result.summary && (
            <div className="ky-card p-4 bg-[#FFFBEB] border-[#FDE68A]">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#D97706] mb-1"><Sparkles size={13}/>AI Stratejik Özet</div>
              <p className="text-sm text-[#1F2937]">{result.summary}</p>
            </div>
          )}

          {/* HEADLINES */}
          <Section
            title="Başlıklar" subtitle={`${headlines.length}/${job.headline_count} · maks. 30 karakter`}
            items={headlines} type="headlines" maxChars={30}
            selected={selectedH} onToggle={(id)=>toggle(selectedH, setSelectedH, id)}
            onSelectAll={()=>selectAll(headlines, setSelectedH)} onClear={()=>clearSel(setSelectedH)}
            onCopyAll={()=>copyAll(headlines)} onCopyOne={copyText}
            onRegen={(id)=>{ setRegenItem({type:"headlines", id}); setRegenInstr(""); }}
          />

          {/* DESCRIPTIONS */}
          <Section
            title="Açıklamalar" subtitle={`${descriptions.length}/${job.description_count} · maks. 90 karakter`}
            items={descriptions} type="descriptions" maxChars={90}
            selected={selectedD} onToggle={(id)=>toggle(selectedD, setSelectedD, id)}
            onSelectAll={()=>selectAll(descriptions, setSelectedD)} onClear={()=>clearSel(setSelectedD)}
            onCopyAll={()=>copyAll(descriptions)} onCopyOne={copyText}
            onRegen={(id)=>{ setRegenItem({type:"descriptions", id}); setRegenInstr(""); }}
          />
        </>
      )}

      {/* Regenerate modal */}
      {regenItem && (
        <Modal onClose={()=>setRegenItem(null)} title={`${regenItem.type === "headlines" ? "Başlığı" : "Açıklamayı"} Yeniden Üret`}>
          <p className="text-xs text-[#64748B] mb-3">AI'a ne istediğini söyle. Boş bırakırsan farklı bir alternatif üretir.</p>
          <textarea value={regenInstr} onChange={e=>setRegenInstr(e.target.value)} rows={3} placeholder="Örn: Daha kısa olsun. Fiyat vurgusunu çıkar. Aciliyet ekle." className="ky-textarea text-sm" autoFocus/>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={()=>setRegenItem(null)} className="ky-btn ky-btn-outline ky-btn-sm">İptal</button>
            <button onClick={regenerate} disabled={regenLoading} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="confirm-regen">
              {regenLoading ? <><Loader2 size={12} className="animate-spin"/>Üretiliyor…</> : <><RefreshCw size={12}/>Yeniden Üret</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Save modal */}
      {saveModal && (
        <Modal onClose={()=>setSaveModal(false)} title="Müşteri Profiline Kaydet">
          <p className="text-xs text-[#64748B] mb-3">Seçilen <strong>{selectedH.size} başlık</strong> ve <strong>{selectedD.size} açıklama</strong> müşteri profiline kaydedilecek.</p>
          <input value={saveName} onChange={e=>setSaveName(e.target.value)} placeholder="Set adı (örn: SEO Mart 2026) *" className="ky-input mb-3" autoFocus data-testid="save-set-name"/>
          {!job.customer_id && (
            <div className="mb-3">
              <label className="block text-xs font-medium text-[#64748B] mb-1.5">Müşteri * <span className="text-[10px] font-normal text-[#94A3B8]">(zorunlu — set profile + portal'da görünecek)</span></label>
              <CustomerSelect customers={allCustomers} value={saveCustomerId || null} onChange={(v) => setSaveCustomerId(v || "")} placeholder="— müşteri seç —" required testid="save-set-customer"/>
              {saveCustomerId ? (
                <p className="text-[10px] text-[#10B981] mt-1">✓ Set müşteri profili + portalında görünecek</p>
              ) : (
                <p className="text-[10px] text-[#EF4444] mt-1">⚠️ Müşteri seçilmedi — set kaydedilemez</p>
              )}
            </div>
          )}
          {job.customer_id && <p className="text-[10px] text-[#10B981] mb-3">✓ Bu iş zaten bir müşteriye bağlı</p>}
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={()=>setSaveModal(false)} className="ky-btn ky-btn-outline ky-btn-sm">İptal</button>
            <button onClick={save} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="confirm-save"><Save size={12}/>Kaydet</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Section = ({ title, subtitle, items, type, maxChars, selected, onToggle, onSelectAll, onClear, onCopyAll, onCopyOne, onRegen }) => (
  <div className="ky-card p-0 overflow-hidden">
    <div className="px-4 py-3 border-b border-[#ECECEF] flex items-center justify-between gap-2 flex-wrap">
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-[11px] text-[#94A3B8]">{subtitle}{selected.size > 0 && ` · ${selected.size} seçili`}</div>
      </div>
      <div className="flex gap-1.5">
        <button onClick={onSelectAll} className="ky-btn ky-btn-outline ky-btn-sm">Tümünü Seç</button>
        {selected.size > 0 && <button onClick={onClear} className="ky-btn ky-btn-ghost ky-btn-sm text-[#EF4444]">Seçimi Temizle</button>}
        <button onClick={onCopyAll} className="ky-btn ky-btn-outline ky-btn-sm"><Copy size={11}/>Tümünü Kopyala</button>
      </div>
    </div>
    {items.length === 0 && <div className="p-10 text-center text-xs text-[#94A3B8]">Üretilmedi</div>}
    {items.map(it => {
      const over = (it.char_count || 0) > maxChars;
      const sel = selected.has(it.id);
      return (
        <div key={it.id} className={`px-4 py-3 border-b border-[#ECECEF] last:border-b-0 flex items-start gap-3 ${sel?"bg-[#EEF2FF]":"hover:bg-[#FAFAFB]"}`} data-testid={`item-${it.id}`}>
          <input type="checkbox" checked={sel} onChange={()=>onToggle(it.id)} className="mt-1 w-4 h-4 accent-[#4F46E5]" data-testid={`checkbox-${it.id}`}/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#0F172A]">{it.text}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${over?"bg-[#FEF2F2] text-[#DC2626]":"bg-[#F4F4F5] text-[#64748B]"}`}>{it.char_count || it.text.length}/{maxChars}</span>
              {it.rationale && <span className="text-[10px] text-[#94A3B8] truncate">— {it.rationale}</span>}
            </div>
          </div>
          <button onClick={()=>onCopyOne(it.text)} className="ky-btn ky-btn-ghost ky-btn-icon" title="Kopyala"><Copy size={12}/></button>
          <button onClick={()=>onRegen(it.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#4F46E5]" title="Yeniden Üret" data-testid={`regen-${it.id}`}><RefreshCw size={12}/></button>
        </div>
      );
    })}
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
    <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e=>e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-base">{title}</h3>
        <button onClick={onClose} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
      </div>
      {children}
    </div>
  </div>
);


const ADCOPY_TIPS = [
  "İpucu: Her başlık en fazla 30, açıklama en fazla 90 karakter olabilir — sistem otomatik denetler.",
  "İpucu: 8 farklı ton seçeneği var: Profesyonel, Samimi, Acil, Lüks, Cesur, Espirili, Bilgilendirici, Duygusal.",
  "İpucu: Beğenmediğin tek bir başlığı/açıklamayı 'Yeniden Üret' ile özel talimat vererek değiştirebilirsin.",
  "İpucu: Beğendiklerini seçip 'Müşteri Setine Kaydet' diyerek müşteri profilinde saklayabilirsin.",
];
const ADCOPY_STEPS = [
  { icon: FileText, label: "İş yükleniyor", hint: "Reklam metni kaydı getiriliyor…" },
  { icon: Sparkles, label: "AI hazırlanıyor", hint: "Claude Sonnet 4.5 yaratıcı modu açıyor…" },
  { icon: ListTree, label: "Varyantlar derleniyor", hint: "15 başlık + 4 açıklama üretilecek…" },
];

const AdCopySkeleton = ({ loadError }) => {
  const [stepIdx, setStepIdx] = React.useState(0);
  const [tipIdx, setTipIdx] = React.useState(0);
  React.useEffect(() => {
    const t1 = setInterval(() => setStepIdx(i => (i + 1) % ADCOPY_STEPS.length), 1800);
    const t2 = setInterval(() => setTipIdx(i => (i + 1) % ADCOPY_TIPS.length), 4000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);
  const Active = ADCOPY_STEPS[stepIdx].icon;
  return (
    <div className="max-w-2xl mx-auto py-12" data-testid="adcopy-job-skeleton">
      <div className="ky-card p-8 text-center">
        <div className="relative w-20 h-20 mx-auto mb-5">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#D97706] to-[#DC2626] opacity-10 animate-pulse"/>
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#D97706] to-[#DC2626] flex items-center justify-center">
            <Active size={32} className="text-white animate-pulse" style={{ animationDuration: "1.5s" }}/>
          </div>
          <div className="absolute -inset-1 rounded-2xl border-2 border-[#D97706] opacity-30 animate-ping" style={{ animationDuration: "2s" }}/>
        </div>
        <h2 className="text-lg font-bold text-[#0F172A]">{ADCOPY_STEPS[stepIdx].label}</h2>
        <p className="text-sm text-[#64748B] mt-1">{ADCOPY_STEPS[stepIdx].hint}</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          {ADCOPY_STEPS.map((s, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === stepIdx ? "w-8 bg-[#D97706]" : "w-1.5 bg-[#E5E7EB]"}`}/>
          ))}
        </div>
        {loadError && (
          <div className={`mt-5 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full ${loadError.kind === "transient" ? "bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" : "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]"}`}>
            {loadError.kind === "transient" ? <Loader2 size={11} className="animate-spin"/> : <AlertCircle size={11}/>}
            <span>{loadError.msg}</span>
          </div>
        )}
        <div className="mt-6 pt-5 border-t border-[#ECECEF]">
          <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2">Bunu biliyor muydun?</div>
          <div className="text-xs text-[#475569] min-h-[2.5em] flex items-center justify-center px-4">
            <span key={tipIdx} className="ky-fade-in">{ADCOPY_TIPS[tipIdx]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
